import React, { ReactNode } from 'react';
import styled from 'styled-components';
import {
  Button, IconType, Skeleton, Theme, Title,
} from '@routific/hive';
import { observer } from 'mobx-react';
import { Space } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { PANEL_BORDER_COLOR } from '../../../constants';
import { ScreenSizes } from '../../../helpers/breakpoints';

interface Props {
  title: ReactNode | string;
  titlePrefix?: ReactNode;
  content: ReactNode | null;
  onClose?: React.MouseEventHandler;
  onBack?: React.MouseEventHandler;
  side?: string;
  loading?: boolean;
  disableBack?: boolean;
}

const Header = styled.div<{ $isScreenMedium: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: inherit;
  padding: ${({ $isScreenMedium }) => ($isScreenMedium ? `${Theme.Spacing.mdSpacing}` : `${Theme.Spacing.smSpacing} ${Theme.Spacing.mdSpacing}`)};
  border-bottom: 1px solid ${PANEL_BORDER_COLOR};
  gap: ${Theme.Spacing.smSpacing};
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
`;

const ContentContainer = styled(HeaderInner)`
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
`;

const TitleContainer = styled(HeaderInner)`
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 0;
  gap: ${Theme.Spacing.smSpacing};
`;

const PanelHeader: React.FC<Props> = ({
  title, titlePrefix, content, onClose, loading, onBack, disableBack,
}) => {
  const isScreenMedium = useMediaQuery({ minWidth: ScreenSizes.md });
  const titleNode = typeof title === 'string' || typeof title === 'number'
    ? <Title level={4} ellipsis>{title}</Title>
    : title;

  return (
    <>
      <Header $isScreenMedium={isScreenMedium}>
        <TitleContainer>
          {!disableBack && onBack && (
          <Button
            type="link"
            size="small"
            icon={IconType.Back}
            style={{ marginRight: Theme.Spacing.smSpacing }}
            onClick={onBack}
            data-cy="back-button"
            data-testId="back-button"
            />
          )}
          {titlePrefix}
          {loading ? (
            <Skeleton active paragraph={{ rows: 1, width: ['100%'] }} />
          ) : (
            titleNode
          )}
        </TitleContainer>
        <ContentContainer>
          {loading ? (
            <>
              <Space size="small">
                <Skeleton active paragraph={{ rows: 1, width: ['20px'] }} />
                <Skeleton active paragraph={{ rows: 1, width: ['20px'] }} />
                <Skeleton active paragraph={{ rows: 1, width: ['20px'] }} />
              </Space>
            </>
          ) : (
            <>
              {content}
            </>
          )}
          {onClose
            && (
            <Button
              type="default"
              size="middle"
              data-cy="close-panel-button"
              style={{ marginLeft: Theme.Spacing.smSpacing }}
              icon={IconType.CollapseRight}
              onClick={onClose}
            />
            )}
        </ContentContainer>
      </Header>
    </>
  );
};

export default observer(PanelHeader);
